// ===========================================
// COMPONENTE: ActividadesSelector
// ===========================================
// Selector de múltiples actividades económicas ARCA
// Permite agregar/eliminar actividades dinámicamente

import { Plus, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { ActividadCombobox } from '@/components/ui/actividad-combobox'
import type { ActividadEconomica } from '@/types/database'

interface ActividadesSelectorProps {
  value: ActividadEconomica[]
  onChange: (actividades: ActividadEconomica[]) => void
  disabled?: boolean
  maxActividades?: number
}

export function ActividadesSelector({
  value = [],
  onChange,
  disabled = false,
  maxActividades = 10,
}: ActividadesSelectorProps) {
  // Agregar nueva actividad vacía
  const handleAgregar = () => {
    if (value.length >= maxActividades) return
    onChange([...value, { codigo: '', descripcion: '' }])
  }

  // Actualizar una actividad específica
  const handleActividadChange = (
    index: number,
    codigo: string | null,
    descripcion: string | null
  ) => {
    const nuevas = [...value]
    if (codigo && descripcion) {
      nuevas[index] = { codigo, descripcion }
    } else {
      // Si se limpia, remover la actividad
      nuevas.splice(index, 1)
    }
    onChange(nuevas)
  }

  // Eliminar una actividad
  const handleEliminar = (index: number) => {
    const nuevas = value.filter((_, i) => i !== index)
    onChange(nuevas)
  }

  return (
    <div className="space-y-3">
      {/* Lista de actividades */}
      {value.length > 0 ? (
        <div className="space-y-2">
          {value.map((actividad, index) => (
            <div key={index} className="flex items-center gap-2">
              {/* Indicador de orden */}
              <div className="flex items-center justify-center w-6 h-6 rounded-full bg-muted text-xs font-medium text-muted-foreground shrink-0">
                {index + 1}
              </div>

              {/* Combobox de actividad */}
              <div className="flex-1">
                <ActividadCombobox
                  value={actividad.codigo || null}
                  onValueChange={(codigo, descripcion) =>
                    handleActividadChange(index, codigo, descripcion)
                  }
                  placeholder={
                    index === 0
                      ? 'Seleccionar actividad principal...'
                      : 'Seleccionar actividad secundaria...'
                  }
                  disabled={disabled}
                />
              </div>

              {/* Botón eliminar */}
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => handleEliminar(index)}
                disabled={disabled}
                className="shrink-0 text-muted-foreground hover:text-destructive"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-sm text-muted-foreground text-center py-2">
          No hay actividades agregadas
        </p>
      )}

      {/* Botón agregar */}
      {value.length < maxActividades && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={handleAgregar}
          disabled={disabled}
          className="w-full"
        >
          <Plus className="h-4 w-4 mr-2" />
          Agregar actividad {value.length === 0 ? 'principal' : 'secundaria'}
        </Button>
      )}

      {/* Mensaje si llegó al máximo */}
      {value.length >= maxActividades && (
        <p className="text-xs text-muted-foreground text-center">
          Máximo {maxActividades} actividades permitidas
        </p>
      )}

      {/* Helper text */}
      {value.length > 0 && (
        <p className="text-xs text-muted-foreground">
          La primera actividad se considera la actividad principal.
        </p>
      )}
    </div>
  )
}
